import { Component, Element, h, Host, Prop } from '@stencil/core';
import warn from '../../../../capabilities/warn';
import FieldDecorator from '../_interfaces/FieldDecorator';
import appendDescribedBy from '../_util/appendDescribedBy';

/**
 * @description form-kit: Applies Design System styles to a fieldset.  A child fieldset must be provided,
 * with an optional legend and a group of form controls inside.
 *
 * @use
 * @version 1
 * @formkit
 * @since 6.0.0
 */
@Component({
  tag: 'hp6-fieldset-decorator',
  styleUrl: 'fieldset-decorator.scss',
  shadow: true,
})
export class FieldsetDecorator implements FieldDecorator {
  /**
   * should display valid or invalid styles
   */
  @Prop({ reflect: true }) isValid: boolean = undefined;

  /**
   * Legend size
   */
  @Prop({ reflect: true }) legendSizeSelection: 'xs' | 'sm' | 'md' | 'lg' = 'xs';

  /**
   * Legend font type
   */
  @Prop({ reflect: true }) legendTypeSelection: 'heading' | 'body' = 'heading';

  // TODO: document fieldset attributes now expected to be on wrapped tag
  /**
   * disabled
   * required
   * name
   * id
   * required - determined by form control set
   * associatedFormId
   */

  @Element() host: HTMLElement;

  get decoratedEl(): HTMLFieldSetElement {
    const el = this.host.querySelector('fieldset');
    if (!el) {
      warn('fieldset-decorator: no fieldset found');
    }
    return el;
  }

  get legendElement(): HTMLLegendElement {
    const { decoratedEl } = this;
    if (decoratedEl) {
      const el = decoratedEl.querySelector('legend');
      if (!el) {
        warn('fieldset-decorator: no legend found');
      }
      return el;
    }
    return null;
  }

  decorate() {
    const { decoratedEl } = this;
    if (decoratedEl) {
      // NOTE that fieldset.validate() returns incorrect info.
      // Need to use CSS selector for the correct answer on fieldset.

      if (!decoratedEl) {
        return;
      }

      const hasId = decoratedEl.getAttribute('id');
      if (!hasId) {
        warn('fieldset-decorator: fieldset does not have an id attribute.');
      }

      // add fieldset to the list of aria-describedby elements
      appendDescribedBy(decoratedEl, `${decoratedEl.id}-aria-describedby`);
    }
  }

  componentDidRender() {
    this.decorate();
  }

  render() {
    const { legendElement, isValid } = this;

    const isValidated = isValid !== undefined;
    const validClass = isValid === true ? 'valid' : 'invalid';

    return (
      <Host
        class={`${legendElement ? 'has-legend' : ''} ${isValidated ? validClass : ''}`}
        data-decorator="fieldset"
      >
        {isValidated
          ? (
              <hp6-icon
                class="validation-icon"
                size-selection="sm"
                name={isValid ? 'system-success' : 'system-error'}
              />
            )
          : null}
        <slot />
      </Host>
    );
  }
}
